import {
  Inject,
  Injectable,
  Optional,
  Renderer2,
  SkipSelf,
  InjectionToken,
  DOCUMENT,
} from "@angular/core";

import { WINDOW } from "./window-token.module";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

/**
 * Uses the current document to create a hidden text area that is used to poulate, select, then copy to clipbaord.
 * The text area is cleared after every copy()
 */
@Injectable()
export class BfxClipboardService {
  private tempTextArea: HTMLTextAreaElement | undefined;

  /**********************************************************************/
  /* CONSTRUCTOR */
  constructor(
    @Inject(DOCUMENT) private document: any,
    @Inject(WINDOW) private window: any,
  ) {}

  /**
   * Is copying to the clipboard supported on this UI configuration?
   * @returns
   */
  public isSupported(): boolean {
    return !!this.document.queryCommandSupported && !!this.document.queryCommandSupported("copy");
  }

  public isTargetValid(element: HTMLInputElement | HTMLTextAreaElement): boolean {
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      if (element.hasAttribute("disabled")) {
        // eslint-disable-next-line max-len
        throw new Error(
          'Invalid "target" attribute. Please use "readonly" instead of "disabled" attribute',
        );
      }
      return true;
    }
    throw new Error("Target should be input or textarea");
  }

  /**
   * Creates a fake textarea element, sets its value from `text` property
   * Promise added for sync.
   */
  public setContent(content: string): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.tempTextArea) {
        this.tempTextArea = this.createTempTextArea(this.document, this.window);
        this.document.body.appendChild(this.tempTextArea);
      }
      this.tempTextArea.value = content;
      resolve(true);
    });
  }

  /**
   * Copy content that was previously set to the system clipboard
   * @returns
   */
  public copyContent(): boolean {
    try {
      if (this.tempTextArea && this.tempTextArea.value.length > 1) {
        this.selectTarget(this.tempTextArea);
        const re = this.copyText();
        this.clearSelection(this.tempTextArea, this.window);
        return re;
      } else {
        throw new Error("Target input or textarea is empty!");
      }
    } catch (error) {
      throw new Error(error);
    }
  }

  // remove temporary textarea if any
  public destroy() {
    if (this.tempTextArea) {
      this.document.body.removeChild(this.tempTextArea);
      this.tempTextArea = undefined;
    }
  }

  /**********************************************************************/
  /* PRIVATE */

  // select the target html input element
  private selectTarget(inputElement: HTMLInputElement | HTMLTextAreaElement): number | undefined {
    // renderer.invokeElementMethod(inputElement, 'select');//ANGULAR UPGRADE CHANGE
    inputElement.select(); //
    // renderer.invokeElementMethod(inputElement, 'setSelectionRange', [0, inputElement.value.length]);//ANGULAR UPGRADE CHANGE
    inputElement.setSelectionRange(0, inputElement.value.length);
    return inputElement.value.length;
  }

  private copyText(): boolean {
    const result = this.document.execCommand("copy");
    if (!result) {
      logger.out.warn("'Copy text to clipboard' operation return false value");
    }
    return result;
  }
  // Removes current selection and focus from `target` element.
  private clearSelection(inputElement: HTMLInputElement | HTMLTextAreaElement, window: Window) {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    inputElement && inputElement.blur();
    window.getSelection().removeAllRanges();
  }

  // create a fake textarea for copy command
  private createTempTextArea(doc: Document, window: Window): HTMLTextAreaElement {
    const isRTL = doc.documentElement.getAttribute("dir") === "rtl";
    let ta: HTMLTextAreaElement;
    ta = doc.createElement("textarea");
    // Prevent zooming on iOS
    ta.style.fontSize = "12pt";
    // Reset box model
    ta.style.border = "0";
    ta.style.padding = "0";
    ta.style.margin = "0";
    // Move element out of screen horizontally
    ta.style.position = "absolute";
    ta.style[isRTL ? "right" : "left"] = "-9999px";
    // Move element to the same position vertically
    let yPosition = window.pageYOffset || doc.documentElement.scrollTop;
    ta.style.top = yPosition + "px";
    ta.setAttribute("readonly", "");
    return ta;
  }
}
